import * as moment from 'moment/moment';
import * as Consts from '@shared/core/consts';
import * as Statics from '@shared/core/statics';
import { Dates } from '@shared/core/utils/dates.utils';
import { ReplacePlaceholderVariables } from './replace-variable-with-value.utils';

interface PickupLabelParams {
    isScheduleOrderingEnabled: boolean;
    type: OLO.Types.PICKUP_LABEL_TYPES;
    date: Date | string;
    isAsap?: boolean;
    format?: Nullable<string>;
    asapMinutesFromNow?: Nullable<number>;
    timeSpan?: Nullable<OLO.Ordering.PickupTimeSpan>;
}

interface NormalizedParams extends PickupLabelParams {
    date: Date;
    format: string;
    timeSpan: Nullable<OLO.Ordering.PickupTimeSpan>;
}

export class PickupLabelGeneratorUtils {
    public static createPickupLabelName(params: PickupLabelParams): string {
        const {
            isScheduleOrderingEnabled,
            type,
            date,
            isAsap = false,
            format = Consts.DATE_DISPLAY_FORMAT,
            asapMinutesFromNow = null,
            timeSpan = null,
        } = PickupLabelGeneratorUtils._normalizeParams(params);

        const { currentTime, daysDiff, minutesFromNow, label } = PickupLabelGeneratorUtils._calculateTimeInfo(date, asapMinutesFromNow, timeSpan);

        if (!isScheduleOrderingEnabled && !timeSpan) {
            return PickupLabelGeneratorUtils._createNonScheduleLabel(type, isAsap, minutesFromNow, label);
        }

        switch (type) {
            case 'orderConfirmation':
                return PickupLabelGeneratorUtils._createOrderConfirmationLabel(daysDiff, label, date, format);
            case 'common':
                return PickupLabelGeneratorUtils._createCommonLabel(daysDiff, isAsap, label, date, format, timeSpan);
            case 'checkoutBox':
                return PickupLabelGeneratorUtils._createCheckoutBoxLabel(daysDiff, isAsap, label, date, format);
            case 'location':
                return PickupLabelGeneratorUtils._createLocationLabel(daysDiff, isAsap, minutesFromNow, label, date, format, timeSpan);
            case 'filter':
                return PickupLabelGeneratorUtils._createFilterLabel(daysDiff, isAsap, label, date, format);
            default:
                return 'undefined case';
        }
    }

    public static createPickUpLabel(value: OLO.Ordering.PickupTime | string, type: OLO.Types.PICKUP_LABEL_TYPES = 'common', format: string = Consts.DATE_DISPLAY_FORMAT): string {
        const config = new Statics.ConfigStatic().current;
        const isScheduleOrderingEnabled = config.onlineOrders.scheduledOrders;

        if (!value) {
            return PickupLabelGeneratorUtils._createDefaultLabel(isScheduleOrderingEnabled, type, format);
        }

        if (typeof value === 'string') {
            return PickupLabelGeneratorUtils._createLabelFromString(value, isScheduleOrderingEnabled, type, format);
        }

        return PickupLabelGeneratorUtils._createLabelFromPickupTime(value, isScheduleOrderingEnabled, type, format);
    }

    private static _normalizeParams(params: PickupLabelParams): NormalizedParams {
        let { date, format, timeSpan, isAsap } = params;

        format = format || Consts.DATE_DISPLAY_FORMAT;
        date = typeof date === 'string' ? Dates.createDate(date) : date;

        if (!(date instanceof Date)) {
            date = new Date();
            isAsap = true;
        }

        if (typeof timeSpan?.endDate === 'string') {
            timeSpan.endDate = Dates.createDate(timeSpan.endDate);
        }

        return { ...params, date, format, timeSpan, isAsap };
    }

    private static _calculateTimeInfo(date: Date, asapMinutesFromNow: number | null, timeSpan: OLO.Ordering.PickupTimeSpan | null) {
        const currentTime = new Date(new Date().setSeconds(0, 0));
        const daysDiff = Dates.datesDiffInDays(date, currentTime);

        let minutesFromNow = Math.max(0, Math.floor((date.getTime() - currentTime.getTime()) / (60 * 1000)));

        if (asapMinutesFromNow !== null) {
            minutesFromNow = asapMinutesFromNow;
        }

        const label = timeSpan?.endDate ? `${Dates.hoursFromDate(date, true, false)} - ${Dates.hoursFromDate(timeSpan.endDate)}` : `${Dates.hoursFromDate(date, true)}`;

        return { currentTime, daysDiff, minutesFromNow, label };
    }

    private static _createNonScheduleLabel(type: OLO.Types.PICKUP_LABEL_TYPES, isAsap: boolean, minutesFromNow: number, label: string): string {
        const text = new Statics.TextsStatic().current;

        if (isAsap) {
            switch (type) {
                case 'common':
                    return text.pickup.asap;
                case 'checkoutBox':
                    return PickupLabelGeneratorUtils._replaceMinutes(text.pickup.inXmins, minutesFromNow);
                case 'location':
                    return minutesFromNow ? PickupLabelGeneratorUtils._replaceMinutes(text.pickup.asapMins, minutesFromNow) : text.pickup.asap;
                case 'filter':
                    return text.pickup.now;
            }
        } else {
            switch (type) {
                case 'common':
                case 'checkoutBox':
                    return PickupLabelGeneratorUtils._replaceLabel(text.pickup.atLabel, label);
                case 'location':
                    return label;
                case 'filter':
                    return PickupLabelGeneratorUtils._replaceLabel(text.pickup.capitalAtLabel, label);
            }
        }

        return label;
    }

    private static _createOrderConfirmationLabel(daysDiff: number, label: string, date: Date, format: string): string {
        const text = new Statics.TextsStatic().current;

        switch (true) {
            case daysDiff === 0:
                return PickupLabelGeneratorUtils._replaceLabel(text.pickup.todayAtLabel, label);
            case daysDiff === 1:
                return PickupLabelGeneratorUtils._replaceLabel(text.pickup.tomorrowAtLabel, label);
            case daysDiff < 7:
                return PickupLabelGeneratorUtils._replaceDayAndLabel(text.pickup.dayAtLabel, Dates.getDayName(date.getDay()), label);
            default:
                return PickupLabelGeneratorUtils._replaceDateAndLabel(text.pickup.dateAtLabel, moment(date).format(format || 'Do of D MMM'), label);
        }
    }

    private static _createCommonLabel(daysDiff: number, isAsap: boolean, label: string, date: Date, format: string, timeSpan: OLO.Ordering.PickupTimeSpan | null): string {
        if (!timeSpan) {
            return PickupLabelGeneratorUtils._createCheckoutBoxLabel(daysDiff, isAsap, label, date, format);
        } else {
            return PickupLabelGeneratorUtils._getFormattedPickupLabel(date, label, daysDiff, format);
        }
    }

    private static _createCheckoutBoxLabel(daysDiff: number, isAsap: boolean, label: string, date: Date, format: string): string {
        const text = new Statics.TextsStatic().current;

        switch (true) {
            case daysDiff === 0:
                return isAsap ? text.pickup.todayAsap : PickupLabelGeneratorUtils._replaceLabel(text.pickup.todayAtLabel, label);
            case daysDiff === 1:
                return PickupLabelGeneratorUtils._replaceLabel(text.pickup.tomorrowAtLabel, label);
            case daysDiff < 7:
                return PickupLabelGeneratorUtils._replaceDayAndLabel(text.pickup.thisDayAtLabel, Dates.getDayName(date.getDay()), label);
            default:
                return PickupLabelGeneratorUtils._replaceDateAndLabel(text.pickup.dateAtLabel, moment(date).format(format), label);
        }
    }

    private static _createLocationLabel(
        daysDiff: number,
        isAsap: boolean,
        minutesFromNow: number,
        label: string,
        date: Date,
        format: string,
        timeSpan: OLO.Ordering.PickupTimeSpan | null,
    ): string {
        const text = new Statics.TextsStatic().current;

        if (!timeSpan) {
            switch (true) {
                case daysDiff === 0:
                    if (isAsap && minutesFromNow) return PickupLabelGeneratorUtils._replaceMinutes(text.pickup.todayAsapMins, minutesFromNow);
                    if (isAsap && !minutesFromNow) return text.pickup.todayAsap;

                    return PickupLabelGeneratorUtils._replaceLabel(text.pickup.todayAtLabel, label);
                case daysDiff === 1:
                    return PickupLabelGeneratorUtils._replaceLabel(text.pickup.tomorrowAtLabel, label);
                case daysDiff < 7:
                    return PickupLabelGeneratorUtils._replaceDayAndLabel(text.pickup.dayAtLabel, Dates.getDayName(date.getDay()), label);
                default:
                    return PickupLabelGeneratorUtils._replaceDateAndLabel(text.pickup.dateAtLabel, moment(date).format(format), label);
            }
        } else {
            return PickupLabelGeneratorUtils._getFormattedPickupLabel(date, label, daysDiff, format);
        }
    }

    private static _getFormattedPickupLabel(date: Date, label: string, daysDiff: number, format: string): string {
        const text = new Statics.TextsStatic().current;

        switch (true) {
            case daysDiff === 0:
                return PickupLabelGeneratorUtils._replaceLabel(`${text.pickup.today} {{label}}`, label);
            case daysDiff === 1:
                return PickupLabelGeneratorUtils._replaceLabel(`${text.pickup.tomorrow} {{label}}`, label);
            case daysDiff < 7:
                return PickupLabelGeneratorUtils._replaceDayAndLabel('{{day}} {{label}}', Dates.getDayName(date.getDay()), label);
            default:
                return PickupLabelGeneratorUtils._replaceDateAndLabel('{{date}} {{label}}', moment(date).format(format), label);
        }
    }

    private static _createFilterLabel(daysDiff: number, isAsap: boolean, label: string, date: Date, format: string): string {
        const text = new Statics.TextsStatic().current;

        switch (true) {
            case daysDiff === 0:
                return isAsap ? text.pickup.now : PickupLabelGeneratorUtils._replaceLabel(text.pickup.labelToday, label);
            case daysDiff === 1:
                return PickupLabelGeneratorUtils._replaceLabel(text.pickup.labelTomorrow, label);
            case daysDiff < 7:
                return `${label} ${Dates.getDayName(date.getDay())}`;
            default:
                return `${label} ${moment(date).format(format)}`;
        }
    }

    private static _replaceMinutes(text: string, minutes: number): string {
        return ReplacePlaceholderVariables.replaceVariableWithValue(text, 'minutesFromNow', `${minutes}`);
    }

    private static _replaceLabel(text: string, label: string): string {
        return ReplacePlaceholderVariables.replaceVariableWithValue(text, 'label', label);
    }

    private static _replaceDayAndLabel(text: string, day: string, label: string): string {
        return ReplacePlaceholderVariables.replaceVariablesWithValues(text, [
            { variable: 'day', value: day },
            { variable: 'label', value: label },
        ]);
    }

    private static _replaceDateAndLabel(text: string, date: string, label: string): string {
        return ReplacePlaceholderVariables.replaceVariablesWithValues(text, [
            { variable: 'date', value: date },
            { variable: 'label', value: label },
        ]);
    }

    private static _createDefaultLabel(isScheduleOrderingEnabled: boolean, type: OLO.Types.PICKUP_LABEL_TYPES, format: string): string {
        return PickupLabelGeneratorUtils.createPickupLabelName({
            isScheduleOrderingEnabled,
            type,
            date: new Date(),
            isAsap: true,
            format,
            asapMinutesFromNow: null,
            timeSpan: null,
        });
    }

    private static _createLabelFromString(value: string, isScheduleOrderingEnabled: boolean, type: OLO.Types.PICKUP_LABEL_TYPES, format: string): string {
        const convertedDate: Date = Dates.createDate(value);
        if (convertedDate.toString() === 'Invalid Date') return value;

        return PickupLabelGeneratorUtils.createPickupLabelName({
            isScheduleOrderingEnabled,
            type,
            date: convertedDate,
            isAsap: Dates.datesDiffInDays(new Date(), convertedDate) === 0,
            format,
            asapMinutesFromNow: null,
            timeSpan: null,
        });
    }

    private static _createLabelFromPickupTime(value: OLO.Ordering.PickupTime, isScheduleOrderingEnabled: boolean, type: OLO.Types.PICKUP_LABEL_TYPES, format: string): string {
        const timeSpan = value.TimeSpan ? { startDate: value.TimeSpan.startDate, endDate: value.TimeSpan.endDate } : null;

        return PickupLabelGeneratorUtils.createPickupLabelName({
            isScheduleOrderingEnabled,
            type,
            date: value.DateLocalISO,
            isAsap: value.IsAsap,
            format,
            asapMinutesFromNow: null,
            timeSpan,
        });
    }
}
